import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import blinkscribeapi from "../../api/blinkscribeapi";
import { eventBus } from "../../utils/eventBus";
import { isAddedOrder } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderKey";
import { v2StoreKey } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderV2";
import {
  SECTION_TITLES, buildSections, concepts, confirmRows, dismissedRecord, dueVisible, emptyRecord, leftoverCount, nothingMissing, nothingMissingText, readRecord,
  reviewEnabled, reviewStoreKey, selection, shownRows, visitKeyOf, writeRecord,
} from "./orderReviewModel";
import { inflightIn } from "./liveOrdersModel";
import { isInflight, waitFlights } from "./liveOrdersVisit";
import { otherWhen, recordWhen } from "./orderWhen";
import WhenToggle from "./WhenToggle";
import "./orderReview.css";

/* The end-of-visit order review screen (owner decision 2026-10-20; model in orderReviewModel.js).

   Mounted once in App.js. ONE screen with every predicted order of the visit filled in; the provider unticks
   what he does not want and presses Confirm once; only then are the ticked orders placed, one at a time,
   through the CareIQ tab's own add (exactly as if he had clicked ⊕ on each, under his session). Cancel places
   nothing. Results show per row; a failed row stays ticked for a retry.

   Who: only visits whose BlinkScribe payload carries review.enabled (BlinkScribe review.py: per provider,
   default Dr. Madhu in 9928, kill switch). Everyone else: nothing here ever opens.

   API (promises, like VisitConsentHost):
     registerOrderReviewVisit(visitKey, ref)   the CareIQ tab of a visit registers ref.current = { payload,
         aliases, place(row) -> Promise<boolean>, fetchOrders() -> Promise<orders[]> } (kept after the tab
         unmounts: its add still works; idempotency never relies on it, see confirmRows)
     openOrderReview({ patientId, apptKey, trigger })  -> { confirmed }      (stop | chart)
     orderReviewAtSignOff({ patientid, appointmentid }) -> "proceed" | "hold"
         signOffNote asks before a sign-off: when the visit's review is still pending (not confirmed) the
         screen opens again (Confirm, Sign without these orders, Not yet). "hold" = the provider chose Not yet.
         No pending review, no host, or the screen disabled: "proceed" at once (sign-off unchanged).
         A review the provider cancelled / closed / signed without (state "dismissed", 10-30) is not pending.
   Leftovers only (owner 10-23): opened by itself (stop / sign-off / the live card's toast) the screen appears ONLY
   when at least one row is left to decide - not already on the visit, not ordered or dismissed in the live Orders
   card (liveOrdersModel.js). Nothing left = it never shows ("review_skipped" is logged; the visit's review counts
   as done, so sign-off is not held). Opened from the chart it always shows; with nothing ticked the button reads
   Close (no "Confirm · no orders").
   Review 10-23: at sign-off the screen is on at once ("Checking this visit's orders…" with Not yet / Sign without
   these orders) and the orders read is raced against ORDERS_TIMEOUT_MS: on a stall it reads "Couldn't load this
   visit's orders" and the provider chooses (never a silent sign). A pending review with no row left to decide (from
   the payload + the record alone) resolves "proceed" without rendering anything.
   The record is shared with the live Orders card: every save re-reads it and merges (never overwrites the card's
   liveOrdered), the screen re-reads it on "orderReviewChanged", and Confirm waits for (or skips) concepts a card
   is still placing, then rebuilds the selection from the re-read record. The title names the patient and the visit time when known (setOrderReviewLabel: the live card's
   chart row, or the recording's patient), so a review opened over another chart is never ambiguous.
   Monitoring: every shown / checked / unchecked / confirmed / cancelled item goes to BlinkScribe's monitor
   (POST /v1/recording/order-suggestions/feedback, concept ids only). */

let hostListener = null;
const registry = new Map();
const labels = new Map();      // visitKey -> { name, time }
let ORDERS_TIMEOUT_MS = 8000;
let FLIGHT_WAIT_MS = 15000;
const TIMED_OUT = { timedOut: true };
// tests only
export const setOrderReviewTimeout = (ms) => { ORDERS_TIMEOUT_MS = Number(ms) > 0 ? Number(ms) : 8000; };
export const setOrderReviewFlightWait = (ms) => { FLIGHT_WAIT_MS = Number(ms) > 0 ? Number(ms) : 15000; };

export const setOrderReviewLabel = (visitKey, label) => {
  if (visitKey && label && (label.name || label.time)) labels.set(String(visitKey), { name: label.name || "", time: label.time || "" });
};
export const orderReviewLabel = (visitKey) => (visitKey ? labels.get(String(visitKey)) || null : null);
// "Maria Delgado · 10:30 AM" (either part may be missing), or ""
export const labelText = (label) => (label ? [label.name, label.time].filter(Boolean).join(" · ") : "");

export const registerOrderReviewVisit = (visitKey, ref) => {
  if (visitKey && ref) registry.set(String(visitKey), ref);
};
export const orderReviewContext = (visitKey) => {
  const ref = visitKey ? registry.get(String(visitKey)) : null;
  return ref && ref.current ? ref.current : null;
};
// tests only
export const resetOrderReviewRegistry = () => { registry.clear(); labels.clear(); };

const storedPayload = (patientId, apptKey) => {
  try {
    const raw = localStorage.getItem(v2StoreKey(patientId, apptKey));
    const st = raw ? JSON.parse(raw) : null;
    return st && st.payload ? st.payload : null;
  } catch (_) {
    return null;
  }
};

const payloadFor = (patientId, apptKey) => {
  const ctx = orderReviewContext(visitKeyOf(patientId, apptKey));
  return (ctx && ctx.payload) || storedPayload(patientId, apptKey);
};

export const openOrderReview = ({ patientId, apptKey, trigger = "chart", mode = "review", label = null } = {}) =>
  new Promise((resolve) => {
    const pid = patientId != null ? String(patientId) : "";
    const appt = apptKey != null ? String(apptKey) : "";
    if (!hostListener || !pid || !appt || !reviewEnabled(payloadFor(pid, appt))) {
      resolve({ confirmed: false, shown: false, action: mode === "signoff" ? "proceed" : "none" });
      return;
    }
    hostListener({ patientId: pid, apptKey: appt, trigger, mode, label, resolve });
  });

// A review still pending for this visit (the screen was offered and never confirmed).
export const reviewPending = (patientId, apptKey) => {
  const rec = readRecord(reviewStoreKey(patientId, apptKey));
  return Boolean(rec && rec.state === "pending" && reviewEnabled(payloadFor(patientId, apptKey)));
};

// Rows left to decide from the payload + the record alone (no orders read): 0 = nothing to review.
const nothingToReview = (pid, appt) => {
  try {
    const ctx = orderReviewContext(visitKeyOf(pid, appt));
    const s = buildSections(payloadFor(pid, appt), {
      rec: readRecord(reviewStoreKey(pid, appt)) || emptyRecord(), aliases: (ctx && ctx.aliases) || {}, visitOrders: [],
    });
    return leftoverCount(s) === 0;
  } catch (_) {
    return false;
  }
};

export const orderReviewAtSignOff = async (visit) => {
  try {
    const pid = visit && visit.patientid != null ? String(visit.patientid) : "";
    const appt = visit && visit.appointmentid != null ? String(visit.appointmentid) : "";
    if (!pid || !appt || !hostListener || !reviewPending(pid, appt)) return "proceed";
    if (nothingToReview(pid, appt)) return "proceed";                 // no flash of an empty screen
    const r = await openOrderReview({ patientId: pid, apptKey: appt, trigger: "signoff", mode: "signoff" });
    return r && r.action === "hold" ? "hold" : "proceed";
  } catch (_) {
    return "proceed";
  }
};

const feedback = (req, event, extra = {}) => {
  if (!req) return;
  const body = { appointmentid: Number(req.apptKey), patientid: Number(req.patientId), event, concepts: [], prechecked: [], ...extra };
  Promise.resolve()
    .then(() => blinkscribeapi.post("/v1/recording/order-suggestions/feedback", body))
    .catch(() => {});
};

const Row = ({ r, status, busy, onToggle, onPick, onWhen }) => (
  <div className={`orv-row${r.checked ? " on" : ""}${status === "failed" ? " failed" : ""}`} data-concept={r.concept} data-section={r.section}>
    <input
      type="checkbox"
      className="orv-box"
      checked={r.checked}
      disabled={busy}
      aria-label={`Order ${r.name}`}
      onChange={(e) => onToggle(r, e.target.checked)}
    />
    <div className="orv-body">
      <div className="orv-line">
        {r.variants.length > 1 ? (
          <select className="orv-variant" aria-label={`Which ${r.name}`} value={r.name} disabled={busy} onChange={(e) => onPick(r, e.target.value)}>
            {r.variants.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        ) : (
          <span className="orv-name">{r.name}</span>
        )}
        {r.evidenceLabel ? <span className={`orv-chip ${r.tone}`}>{r.evidenceLabel}</span> : null}
        {/* Today / Schedule (owner 10-24): placed with this mode on Confirm */}
        <span className="orv-when"><WhenToggle name={r.name} when={r.when} disabled={busy} onToggle={() => onWhen(r)} /></span>
        {status === "placing" ? <span className="orv-st">Placing…</span> : null}
        {status === "failed" ? <span className="orv-st err" role="status">Not placed · still checked to retry</span> : null}
      </div>
      {r.reason ? <div className="orv-why">{r.reason}</div> : null}
    </div>
  </div>
);

const OrderReviewHost = () => {
  const [req, setReq] = useState(null); // { patientId, apptKey, trigger, mode, resolve }
  const [rec, setRec] = useState(null);
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState(() => new Map()); // row id -> placing | placed | failed
  const [busy, setBusy] = useState(false);
  // "Already ordered" rows' Today / Schedule switch: "<visit>|<row id>" -> { when, busy, error }; reset whenever a
  // review opens or finishes (review 10-24 H2: never carried to another visit)
  const [placedWhen, setPlacedWhen] = useState(() => new Map());
  const [round, setRound] = useState(0);
  const reqRef = useRef(null);
  const busyRef = useRef(false);
  const finishRef = useRef(null);          // finish (declared below)
  const timerRef = useRef(null);           // the orders-read timeout (cleared on finish / unmount)
  const [loadFailed, setLoadFailed] = useState(false);

  const storeKey = req ? reviewStoreKey(req.patientId, req.apptKey) : null;
  const ctx = req ? orderReviewContext(visitKeyOf(req.patientId, req.apptKey)) : null;
  const payload = req ? payloadFor(req.patientId, req.apptKey) : null;
  const ctxAliases = ctx && ctx.aliases;
  const aliases = useMemo(() => ctxAliases || {}, [ctxAliases]);
  const isAdded = useCallback(
    (key) => Array.isArray(orders) && isAddedOrder(key, { aliases, currentOrders: orders }),
    [orders, aliases],
  );
  const sections = useMemo(
    () => (req && rec ? buildSections(payload, { rec, aliases, isAdded, visitOrders: orders || [] }) : null),
    [req, rec, payload, aliases, isAdded, orders],
  );

  const freshOrders = useCallback(async (c) => {
    if (!c || typeof c.fetchOrders !== "function") return null;
    try {
      const list = await c.fetchOrders();
      return Array.isArray(list) ? list : null;
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    hostListener = (next) => {
      if (reqRef.current) {               // one screen at a time
        next.resolve({ confirmed: false, shown: false, action: next.mode === "signoff" ? "proceed" : "none" });
        return;
      }
      reqRef.current = next;
      const key = reviewStoreKey(next.patientId, next.apptKey);
      const r = readRecord(key) || emptyRecord();
      if (r.state !== "confirmed") r.state = "pending";
      writeRecord(key, r);
      setRec(r);
      setOrders(null);
      setStatus(new Map());
      setPlacedWhen(new Map());
      setRound(0);
      setLoadFailed(false);
      setReq(next);
      // a stalled orders read (careiqapi has no timeout) is raced against ORDERS_TIMEOUT_MS
      const timeout = new Promise((res) => { timerRef.current = setTimeout(() => res(TIMED_OUT), ORDERS_TIMEOUT_MS); });
      Promise.race([freshOrders(orderReviewContext(visitKeyOf(next.patientId, next.apptKey))), timeout]).then((list) => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        if (reqRef.current !== next) return;
        if (list === TIMED_OUT && next.mode === "signoff") {
          // never a silent sign: the screen says so and keeps Not yet / Sign without these orders
          setLoadFailed(true);
          return;
        }
        setOrders(list && list !== TIMED_OUT ? list : []);
      });
    };
    return () => {
      hostListener = null;
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [freshOrders]);

  const finish = useCallback((result) => {
    const r = reqRef.current;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    reqRef.current = null;
    setLoadFailed(false);
    setReq(null);
    setRec(null);
    setOrders(null);
    setStatus(new Map());
    setPlacedWhen(new Map());
    if (r) r.resolve(result);
  }, []);
  finishRef.current = finish;

  // Opened by itself with nothing left to decide: never shown (owner 10-23, no empty panels).
  const auto = Boolean(req && req.trigger !== "chart");
  const skipNow = Boolean(auto && sections && orders !== null && leftoverCount(sections) === 0);
  useEffect(() => {
    if (!skipNow) return;
    const r0 = reqRef.current;
    const key = r0 ? reviewStoreKey(r0.patientId, r0.apptKey) : null;
    const cur = (key && readRecord(key)) || rec || emptyRecord();
    if (key) writeRecord(key, { ...cur, state: "confirmed" });
    feedback(r0, "review_skipped", { skipped: concepts(sections.placed), placed_n: sections.placed.length, trigger: r0 && r0.trigger });
    eventBus.emit("orderReviewChanged");
    finish({ confirmed: false, shown: false, action: r0 && r0.mode === "signoff" ? "proceed" : "none" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipNow]);

  // One "shown" line per opening, once the visit's orders are known.
  const shownLogged = useRef(null);
  useEffect(() => {
    if (!req || !sections || orders === null || shownLogged.current === req || skipNow) return;
    shownLogged.current = req;
    const rows = shownRows(sections);
    feedback(req, "review_shown", {
      concepts: concepts(rows), prechecked: concepts(rows.filter((r) => r.checked)),
      due: concepts(dueVisible(sections) ? sections.due : []), skipped: concepts(sections.placed), trigger: req.trigger,
      placed_n: sections.placed.length,
    });
  }, [req, sections, orders, skipNow]);

  // every write starts from the record as it is NOW (the live card may have written since this screen read it)
  const latestRec = useCallback(() => (storeKey && readRecord(storeKey)) || rec || emptyRecord(), [storeKey, rec]);
  const save = useCallback((next) => {
    writeRecord(storeKey, next);
    setRec(next);
    eventBus.emit("orderReviewChanged");
  }, [storeKey]);
  useEffect(() => {
    if (!storeKey) return undefined;
    const on = () => {
      const r = readRecord(storeKey);
      if (r && reqRef.current) setRec(r);
    };
    eventBus.on("orderReviewChanged", on);
    return () => eventBus.off("orderReviewChanged", on);
  }, [storeKey]);

  const onToggle = useCallback((row, on) => {
    if (!rec) return;
    const cur = latestRec();
    save({ ...cur, checks: { ...cur.checks, [row.id]: on } });
    feedback(reqRef.current, on ? "review_checked" : "review_unchecked", { concepts: [row.concept], section: row.section, trigger: reqRef.current && reqRef.current.trigger });
  }, [rec, save, latestRec]);

  const onPick = useCallback((row, name) => {
    if (!rec) return;
    const cur = latestRec();
    save({ ...cur, picks: { ...cur.picks, [row.id]: name } });
    feedback(reqRef.current, "review_variant", { concepts: [row.concept], section: row.section });
  }, [rec, save, latestRec]);

  const onWhen = useCallback((row) => {
    if (!rec || !row.concept) return;
    const next = otherWhen(row.when);
    save(recordWhen(latestRec(), row.concept, next));
    feedback(reqRef.current, "review_when", {
      concepts: [row.concept], section: row.section, when: next, when_default: row.whenDefault || undefined,
      trigger: reqRef.current && reqRef.current.trigger,
    });
  }, [rec, save, latestRec]);

  // An "Already ordered" row's Today / Schedule (owner 10-24): the dashboard's existing call through the visit's
  // context (scoped to this visit's orders, read back); the row shows the result or the error.
  const pwKey = (p) => `${req ? visitKeyOf(req.patientId, req.apptKey) : ""}|${p.id}`;
  const canSetWhen = Boolean(ctx && typeof ctx.setWhen === "function" && ctx.whenEditable === true);
  const onPlacedWhen = useCallback(async (p) => {
    if (!canSetWhen || p.orderId == null || !req) return;
    const k = `${visitKeyOf(req.patientId, req.apptKey)}|${p.id}`;
    const cur = placedWhen.get(k) || {};
    if (cur.busy) return;
    const next = otherWhen(cur.when || p.when);
    setPlacedWhen((m) => new Map(m).set(k, { when: cur.when || p.when, busy: true, error: null }));
    let r = { ok: false, error: "Not changed · try again" };
    try { r = await ctx.setWhen(p.orderId, next); } catch (_) { /* error stays */ }
    if (reqRef.current !== req) return;                    // the review closed / another visit opened meanwhile
    setPlacedWhen((m) => new Map(m).set(k, r.ok ? { when: next, busy: false, error: null } : { when: cur.when || p.when, busy: false, error: r.error || "Not changed · try again" }));
    if (r.ok) {
      if (p.concept) save(recordWhen(latestRec(), p.concept, next));
      feedback(reqRef.current, "review_when", { concepts: p.concept ? [p.concept] : [], section: undefined, when: next, placed_n: 1 });
    }
  }, [ctx, canSetWhen, req, placedWhen, save, latestRec]);

  const canPlace = Boolean(ctx && typeof ctx.place === "function");

  const onConfirm = useCallback(async () => {
    const r0 = reqRef.current;
    if (!r0 || !sections || busyRef.current) return;
    if (selection(sections).length === 0) {
      // nothing ticked: Close (owner 10-23: no "Confirm · no orders"); the review counts as done
      const cur = readRecord(storeKey) || rec || emptyRecord();
      writeRecord(storeKey, { ...cur, state: "confirmed" });
      feedback(r0, "review_confirmed", { concepts: [], unchecked: concepts(shownRows(sections)), trigger: r0.trigger });
      eventBus.emit("orderReviewChanged");
      finish({ confirmed: true, shown: true, action: "proceed", placed: [] });
      return;
    }
    if (!canPlace) return;
    busyRef.current = true;
    setBusy(true);
    try {
      // a live card still placing one of the ticked concepts (this page or another tab): wait for it, then skip
      // what is still in flight - never a second placement of the same order
      const vk = visitKeyOf(r0.patientId, r0.apptKey);
      const ticked = concepts(selection(sections));        // what the provider saw ticked when he pressed Confirm
      const stuck = await waitFlights(vk, ticked, {
        timeoutMs: FLIGHT_WAIT_MS, inOtherTab: (c) => inflightIn(readRecord(storeKey), c),
      });
      // idempotency: the visit's orders re-read right before placing, plus this visit's review ledger, and the
      // selection rebuilt from the record as it is now (a card tap that finished meanwhile is not placed again)
      const list = await freshOrders(ctx);
      const current = list || orders || [];
      const added = (key) => isAddedOrder(key, { aliases, currentOrders: current });
      let working = latestRec();
      const secs = buildSections(payload, { rec: working, aliases, isAdded: added, visitOrders: current });
      const ledger = { ...(working.placed || {}) };
      const sel = selection(secs).filter((x) => !stuck.includes(x.concept) && !isInflight(vk, x.concept));
      const unchecked = shownRows(secs).filter((x) => !x.checked);
      // ticked on screen but already on the visit / ordered by the card by now: skipped (never placed again)
      const selNow = new Set(concepts(sel));
      const gone = ticked.filter((c) => !selNow.has(c) && !stuck.includes(c));
      setRec(working);
      const res = await confirmRows(sel, {
        place: (row) => {
          setStatus((prev) => new Map(prev).set(row.id, "placing"));
          return ctx.place(row);
        },
        ledger,
        isAdded: added,
        onResult: (row, st) => {
          setStatus((prev) => new Map(prev).set(row.id, st));
          if (st === "placed" || st === "skipped") {
            const cur = readRecord(storeKey) || working;
            working = { ...cur, placed: { ...(cur.placed || {}), [row.id]: { key: row.key } } };
            writeRecord(storeKey, working);
          }
        },
      });
      if (list) setOrders(list);
      const done = res.failed.length === 0 && stuck.length === 0;
      working = { ...(readRecord(storeKey) || working), state: done ? "confirmed" : "pending" };
      writeRecord(storeKey, working);
      setRec(working);
      feedback(r0, "review_confirmed", {
        concepts: concepts(sel), placed: concepts(res.placed), failed: concepts(res.failed),
        skipped: [...new Set([...concepts(res.skipped), ...gone])], unchecked: concepts(unchecked),
        due: concepts(sel.filter((x) => x.section === "due")), trigger: round > 0 ? "retry" : r0.trigger,
      });
      setRound((n) => n + 1);
      eventBus.emit("orderReviewChanged");
      if (done) finish({ confirmed: true, shown: true, action: "proceed", placed: concepts(res.placed) });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [sections, canPlace, freshOrders, ctx, orders, aliases, storeKey, rec, round, finish, payload, latestRec]);

  const onCancel = useCallback((action) => {
    const r0 = reqRef.current;
    if (busyRef.current) return;
    if (r0 && sections) {
      feedback(r0, "review_cancelled", { concepts: concepts(shownRows(sections)), unchecked: concepts(shownRows(sections).filter((x) => !x.checked)), trigger: r0.trigger });
    }
    // Cancel / close / "Sign without these orders" is the provider's answer for this visit (10-30, Jaime Nix: the
    // screen came back at sign-off with the same rows ticked, she pressed Confirm and deleted the orders): the
    // review is "dismissed", so sign-off never reopens it, and every row she did not tick herself is stored
    // unticked, so a review reopened from the chart shows nothing pre-ticked. "Not yet" keeps it pending.
    if (r0 && action !== "hold") {
      const key = reviewStoreKey(r0.patientId, r0.apptKey);
      const cur = readRecord(key) || rec || emptyRecord();
      writeRecord(key, dismissedRecord(cur, sections));
      eventBus.emit("orderReviewChanged");
    }
    finish({ confirmed: false, shown: true, action });
  }, [sections, finish, rec]);

  if (!req || !sections) return null;
  const signoff = req.mode === "signoff";
  // opened by itself: never when nothing is left; nothing on screen until the visit's orders are known - except at
  // sign-off, where the provider is waiting on this screen (review 10-23: it shows "Checking…" with its buttons)
  if (skipNow || (auto && orders === null && !signoff)) return null;
  const loading = orders === null;
  const sel = selection(sections);
  const who = labelText(req.label || orderReviewLabel(visitKeyOf(req.patientId, req.apptKey)));
  const failedN = sel.filter((x) => status.get(x.id) === "failed").length;
  const showDue = dueVisible(sections) && sections.due.length > 0;
  const rowsOf = (rows) => rows.map((r) => <Row key={r.id} r={r} status={status.get(r.id)} busy={busy} onToggle={onToggle} onPick={onPick} onWhen={onWhen} />);
  const group = (id, rows) => (rows.length ? (
    <section className="orv-sec" data-testid={`orv-${id}`} key={id}>
      <h3 className="orv-h">{SECTION_TITLES[id]}</h3>
      {rowsOf(rows)}
    </section>
  ) : null);
  const dueGuideline = sections.due.filter((r) => r.line !== "interval");
  const dueInterval = sections.due.filter((r) => r.line === "interval");
  const confirmText = busy
    ? "Placing…"
    : failedN > 0
      ? `Retry ${failedN} not placed`
      : sel.length
        ? `Confirm ${sel.length} order${sel.length === 1 ? "" : "s"}`
        : "Close";

  return (
    <Dialog open onClose={() => onCancel(signoff ? "hold" : "none")} maxWidth="sm" fullWidth aria-labelledby="orv-title">
      <DialogTitle id="orv-title">
        {signoff ? "Orders from this visit are not confirmed" : "Review orders for this visit"}
        {who ? <div className="orv-who" data-testid="orv-who" style={{ fontSize: 13, fontWeight: 500, color: "#4B5563" }}>{who}</div> : null}
      </DialogTitle>
      <DialogContent dividers className="orv" data-testid="order-review" data-loading={loading ? "1" : undefined}>
        <p className="orv-lead">
          Untick anything you do not want. Nothing is ordered until you press Confirm.
        </p>
        {loading && !loadFailed ? <div className="orv-note" role="status" data-testid="orv-checking">Checking this visit’s orders…</div> : null}
        {loading && loadFailed ? (
          <div className="orv-note warn" role="status" data-testid="orv-load-failed">Couldn’t load this visit’s orders.</div>
        ) : null}
        {!loading && !canPlace ? (
          <div className="orv-note warn" role="status">Open this patient’s chart to place these orders.</div>
        ) : null}
        {loading && signoff ? null : group("heard", sections.heard)}
        {loading && signoff ? null : group("suggested", sections.suggested)}
        {showDue && !(loading && signoff) ? (
          <section className="orv-sec" data-testid="orv-due">
            <h3 className="orv-h">{SECTION_TITLES.due}</h3>
            {rowsOf(dueGuideline)}
            {dueInterval.length ? (
              <div className="orv-subline" data-testid="orv-due-interval">
                <div className="orv-subh">Also due by your usual interval</div>
                {rowsOf(dueInterval)}
              </div>
            ) : null}
          </section>
        ) : null}
        {orders !== null && nothingMissing(sections) ? (
          <div className="orv-nothing" role="status" data-testid="orv-nothing-missing">
            {nothingMissingText(sections)}
          </div>
        ) : null}
        {sections.placed.length && !(loading && signoff) ? (
          <section className="orv-sec placed" data-testid="orv-placed">
            <h3 className="orv-h">{SECTION_TITLES.placed}</h3>
            {sections.placed.map((p) => {
              const now = Boolean(p.concept) && (status.get(`s:${p.concept}`) === "placed" || status.get(`d:${p.concept}`) === "placed");
              return (
                <div className="orv-row placed" key={p.id} data-concept={p.concept || undefined} data-section="placed">
                  {/* his own order: shown ticked so the list reads complete, never selectable, never placed again */}
                  <input type="checkbox" className="orv-box" checked readOnly disabled aria-label={`${p.name} already ordered`} />
                  <span className="orv-name">{p.name}</span>
                  <span className="orv-chip placed">{now ? "Placed now" : "Already ordered"}</span>
                  {p.heard && !now ? <span className="orv-chip heard" data-testid="orv-heard-tag">✓ heard in visit</span> : null}
                  {p.orderId != null && p.when ? (
                    <span className="orv-when">
                      <WhenToggle
                        name={p.name}
                        when={(placedWhen.get(pwKey(p)) || {}).when || p.when}
                        readOnly={!canSetWhen}
                        disabled={busy || (placedWhen.get(pwKey(p)) || {}).busy}
                        onToggle={() => onPlacedWhen(p)}
                      />
                    </span>
                  ) : null}
                  {(placedWhen.get(pwKey(p)) || {}).error ? <span className="orv-st err" role="alert">{placedWhen.get(pwKey(p)).error}</span> : null}
                </div>
              );
            })}
          </section>
        ) : null}
        {sections.discussed.length && !(loading && signoff) ? (
          <section className="orv-sec muted" data-testid="orv-discussed">
            <h3 className="orv-h">{SECTION_TITLES.discussed}</h3>
            {sections.discussed.map((d) => (
              <div className="orv-row muted" key={d.id} data-concept={d.concept}>
                <span className="orv-name">{d.name}</span>
                <span className="orv-chip discussed">{d.evidenceLabel}</span>
              </div>
            ))}
          </section>
        ) : null}
      </DialogContent>
      <DialogActions>
        {signoff ? (
          <>
            <Button onClick={() => onCancel("hold")} disabled={busy}>Not yet</Button>
            <Button onClick={() => onCancel("proceed")} disabled={busy}>Sign without these orders</Button>
          </>
        ) : (
          <Button onClick={() => onCancel("none")} disabled={busy}>Cancel</Button>
        )}
        <Button variant="contained" onClick={onConfirm} disabled={busy || (sel.length > 0 && !canPlace) || orders === null} data-testid="orv-confirm">
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderReviewHost;
